import { createContext, useState, useContext } from "react";
import { useResource } from "./ResourceContext";
import { useMessages } from "./MessageContext";
import { useAssistant } from "./AssistantContext";
import { debugLog } from "../../../configs/loggingConfig";

const TemplateContext = createContext();

function TemplateProvider({ children }) {

    const [isRunning, setIsRunning] = useState(false);
    const [templateId, setTemplateId] = useState(0);
    const { updateAssistant, assistants } = useAssistant();
    const { handleFilesUpload } = useResource();
    const { sendMessage } = useMessages();

    // Function for uploading files and then sending messages.
    const executeTemplate = async (files = [], messages = [], messageOverrides = []) => {
        // isRunning state can be used to do something like a popup that indicates when the template is generating.
        setIsRunning(true);
        debugLog.log("[TEMPLATE] Initiating the template.")
        let assistant = { ...assistants.find((opt) => opt.displayName === "Custom") }
        assistant.systemMessage = "You are an AI assistant specialized in reviewing, analyzing, and summarizing Request for Proposal(RFP) documents.Your primary function is to help our proposal teams and decision - makers understand the critical aspects of each RFP efficiently.You must extract key information, summarize complex requirements clearly, and identify potential issues, ambiguities, or risks.You should never guess or assume meaning beyond what is explicitly stated in the text.When language is unclear or open to multiple interpretations, outline all plausible readings without selecting one.Your output should be precise, objective, and well - organized.Use clear and concise language, formatted in logical sections such as Scope of Work, Submission Requirements, Deadlines, Evaluation Criteria, and Risk Flags.Each section should be no more than a short paragraph or 3 - 5 bullet points unless directed otherwise.Cite page numbers or section headings when referencing specific content.When summarizing an RFP, identify and report the following critical details whenever present: submission deadline, contract term, contract value or estimated spend, scope of work, major deliverables, bidder eligibility requirements, required submission components(e.g., forms, pricing templates, certifications), and instructions for submitting questions.Also flag whether the submission must be digital or physical, the submission platform, and any compliance items such as bonding, insurance, or exclusivity terms.In addition to summarizing content, highlight any areas of concern, such as vague requirements, missing evaluation criteria, or contract language that may pose legal or operational risks.When a clause appears to be unusual or overly restrictive, make note of it and explain why it may warrant further review by a legal or contracts team.Do not attempt to paraphrase legal clauses beyond their plain meaning unless explicitly requested.Maintain a neutral and analytical tone throughout.Avoid filler, speculation, or sales - oriented language.Your goal is to enable quick understanding and informed decision - making while minimizing oversight risks.If any expected section of the RFP is absent, clearly indicate this rather than inferring its contents."
        await updateAssistant(assistant);

        // Uploading any files that need to be uploaded.
        if (files && files.length !== 0) {
            debugLog.log("[TEMPLATE] Uploading the files...")
            await handleFilesUpload(files);
        }

        // Sending any messages that need to be sent. Needs to be done one at a time, hence the for loop.
        if (messages && messages.length !== 0) {
            for (let i = 0; i < messages.length; i++) {
                debugLog.log("[TEMPLATE] Sending message", i+1, "of", messages.length);
                try {
                    // Send the message, supply an override if there is a valid one for the message.
                    await sendMessage(messages[i], (messageOverrides.length > i ? messageOverrides[i] : null));
                }
                catch (e) {
                    debugLog.log("[TEMPLATE] Error, terminated at message ", i+1);
                    setIsRunning(false);
                    return;
                }
            }
        }

        setIsRunning(false);
    }

	return (
		<TemplateContext.Provider
			value={{
                // States
                templateId,
                setTemplateId,
                isRunning,
                // Function.
                executeTemplate,
			}}
		>
			{children}
		</TemplateContext.Provider>
	);
}

// Hook to use the TemplateContext in a component
function useTemplate() {
	const context = useContext(TemplateContext);
	if (context === undefined) {
		throw new Error(
			"useTemplate must be used within a TemplateProvider"
		);
	}
	return context;
}

export { TemplateProvider, useTemplate };
